import React, { useEffect, useMemo, useState } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import { changesApi } from '../../../services/changesApi';
import type { ChangeType } from '../../../types/changes';
import { getApiErrorMessage } from '../../../utils/apiError';

export interface ImmediateManagerPickerOption {
  group_id: number;
  group_name?: string | null;
  manager_user_id: number;
  manager_name?: string | null;
  manager_email?: string | null;
}

interface Props {
  id: string;
  label: string;
  requesterId: number;
  changeType: ChangeType | string;
  value: number;
  onChange: (groupId: number) => void;
  disabled?: boolean;
  /** Chamado quando a API define se o tipo usa gerente imediato (após carregar). */
  onApplicableKnown?: (usesImmediateManager: boolean) => void;
}

function optionLabel(item: ImmediateManagerPickerOption): string {
  const group = item.group_name || `Grupo #${item.group_id}`;
  const person = item.manager_name || `Usuário #${item.manager_user_id}`;
  const email = item.manager_email ? ` · ${item.manager_email}` : '';
  return `${group} — ${person}${email}`;
}

const ChangeImmediateManagerPickInput: React.FC<Props> = ({
  id,
  label,
  requesterId,
  changeType,
  value,
  onChange,
  disabled,
  onApplicableKnown,
}) => {
  const [loading, setLoading] = useState(() => Boolean(requesterId && changeType));
  const [error, setError] = useState<string | null>(null);
  const [options, setOptions] = useState<ImmediateManagerPickerOption[]>([]);
  const [usesImmediateManager, setUsesImmediateManager] = useState(false);

  useEffect(() => {
    if (!requesterId || !changeType) {
      setOptions([]);
      setUsesImmediateManager(false);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    changesApi
      .getRequesterManagerOptions(requesterId, String(changeType))
      .then((payload) => {
        if (cancelled) return;
        const uses = Boolean(payload.uses_immediate_manager);
        setUsesImmediateManager(uses);
        setOptions((payload.picker_options || []) as ImmediateManagerPickerOption[]);
        onApplicableKnown?.(uses);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setOptions([]);
        setUsesImmediateManager(true);
        onApplicableKnown?.(true);
        setError(getApiErrorMessage(err, 'Não foi possível carregar gerentes imediatos.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [requesterId, changeType]);

  useEffect(() => {
    if (options.length === 1 && !value) {
      onChange(options[0].group_id);
    }
  }, [options, value, onChange]);

  const selected = useMemo(
    () => options.find((item) => item.group_id === value) || null,
    [options, value],
  );

  if (!requesterId) {
    return (
      <Form.Text className="text-muted d-block">
        Selecione o solicitante para listar os gerentes imediatos.
      </Form.Text>
    );
  }

  if (!usesImmediateManager && !loading) {
    return null;
  }

  if (loading) {
    return (
      <div className="d-flex align-items-center gap-2 text-muted small">
        <Spinner animation="border" size="sm" aria-hidden />
        Carregando gerentes…
      </div>
    );
  }

  if (error) {
    return <Form.Text className="text-danger d-block">{error}</Form.Text>;
  }

  if (!options.length) {
    return (
      <Form.Text className="text-danger d-block" role="alert">
        Não há gerente imediato disponível para este solicitante. Cadastre gestor ou gerente
        imediato em <strong>Cadastros → Grupos → Pessoas</strong> (grupos em que o solicitante
        participa). Não é possível avançar enquanto o campo for obrigatório.
      </Form.Text>
    );
  }

  return (
    <div>
      <Form.Select
        id={id}
        aria-label={label}
        value={value || ''}
        disabled={disabled || options.length <= 1}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
      >
        <option value="">Selecione…</option>
        {options.map((item) => (
          <option key={item.group_id} value={item.group_id}>
            {optionLabel(item)}
          </option>
        ))}
      </Form.Select>
      {selected ? (
        <Form.Text className="text-muted d-block mt-1">
          Grupo: <strong>{selected.group_name || `#${selected.group_id}`}</strong>
          {selected.manager_email ? (
            <>
              {' '}
              · E-mail do gerente: <strong>{selected.manager_email}</strong>
            </>
          ) : null}
        </Form.Text>
      ) : null}
    </div>
  );
};

export default ChangeImmediateManagerPickInput;
